import { classifyHttpError, clientError, unauthorizedError, type ApiError } from '../http/errors.ts';
import type { HttpClient, HttpRequest, HttpResponse } from '../http/types.ts';
import type { InstanceConfig } from '../instance/config.ts';
import { originOf } from '../instance/urls.ts';
import { classifyAuthFailure } from './failure.ts';
import { authEndpointOf } from './scopes.ts';

/**
 * R9 — core models the *mode*, never the credential.
 *
 * The platform owns the token: where it is stored, how it survives a reload, when it is deleted.
 * All core is handed is a function that either produces a header or does not, which is what lets
 * the same core run against `localStorage` on web, the keystore on Android and a literal in a test.
 */
export interface AuthProvider {
  /** `{}` when signed out. Called per request, never cached — the token can be revoked mid-session. */
  headers(): Readonly<Record<string, string>>;
}

/** The signed-out provider, so "no auth" is a value rather than an `undefined` to guard against. */
export const ANONYMOUS_AUTH: AuthProvider = { headers: () => ({}) };

export function bearerAuth(token: string): AuthProvider {
  // R9: raw JSON in the header, not base64. Built here so no caller has to remember that.
  return { headers: () => ({ Authorization: `Bearer ${token}` }) };
}

/**
 * R9's origin assertion, as a decidable predicate.
 *
 * The token is a bearer credential: whoever receives it can use it. Attaching it to a request that
 * escaped to another origin would hand a third party the user's account, and the paths this app
 * handles make that a live risk rather than a theoretical one — `rewriteAssetUrl` deliberately
 * produces googlevideo and ggpht URLs, and the player's own networking follows `<BaseURL>`.
 *
 * So the check is positive: the header goes on **only** when the request origin is exactly the
 * configured transport origin. A relative URL is not resolved here — it is refused, because
 * "resolve it and hope" is how the assertion gets bypassed.
 */
export function mayAttachAuth(config: InstanceConfig, url: string): boolean {
  const target = originOf(url);
  if (target === null) return false;
  return target === originOf(config.transportBaseUrl);
}

/**
 * A non-2xx classified with §8's overloaded `403` in mind. Use this for authenticated API traffic;
 * use {@link classifyHttpError} for media, where a `403` really does mean the URLs expired.
 *
 * **The body goes through to the delegate, not just to the auth check.** This is the classifier the
 * app actually installs for every API request, so anything it drops on the way down is dropped for
 * all of them — and `classifyHttpError` needs the body too, to tell Invidious throwing on one video
 * from the instance being unwell. Passing only `(status, url)` here left that arm dead in the app
 * while every direct unit test of it stayed green (found in a browser, by counting eight
 * retries of a request that should never have been retried once).
 */
export function classifyAuthenticatedHttpError(status: number, url: string, body: string): ApiError {
  const auth = classifyAuthFailure(status, body);
  if (auth !== null) return unauthorizedError(status, url, auth);

  const classified = classifyHttpError(status, url, body);
  /*
   * **A `403` from `/api/v1/auth/*` that is not one of the five auth failures is still not an expired
   * media URL**, and the case below proves it is worth saying so. `/api/v1/auth/feed`
   * refuses two arithmetic inputs with a leaked Postgres error at 403:
   *
   *   GET /api/v1/auth/feed?page=0            → 403 {"error":"OFFSET must not be negative"}
   *   GET /api/v1/auth/feed?page=1000000000   → 403 {"error":"Arithmetic overflow"}
   *
   * `classifyAuthFailure` correctly declines both (neither is an auth message), and `classifyHttpError`
   * then reads the bare 403 as `urls-expired` — so a paging bug in this app would report that *the
   * media URLs expired*, on a page with no media on it, and the recovery path §7 built for a real
   * expiry would be the thing that ran. That is `isEndpointDisabled`'s finding in a fourth place, and
   * the fix generalises where a body regex would not: **no media is ever served from
   * `/api/v1/auth/*`**, so on that path the reading is wrong for every body, including the ones
   * Invidious has not leaked yet.
   *
   * Narrow on purpose. Only the one wrong answer is replaced, so `endpoint-disabled`, the two
   * upstream-failure arms and every other status keep the readings they were measured into — and media
   * cannot reach this function at all (it installs `classifyHttpError`), which is what makes the
   * correction safe rather than a second policy.
   */
  if (classified.kind === 'urls-expired' && authEndpointOf(url) !== null) {
    return clientError(status, url);
  }
  return classified;
}

export interface AuthenticatedHttpDeps {
  readonly http: HttpClient;
  readonly config: InstanceConfig;
  readonly auth: AuthProvider;
}

/**
 * Wraps the platform's {@link HttpClient} so every instance-bound request carries the token and
 * nothing else ever does. Sits *below* the transport, so retry and backoff are unchanged and there
 * is exactly one auth policy rather than a second one bolted alongside.
 *
 * A caller-supplied `Authorization` header wins, which is what makes a deliberately different
 * credential — a tampered token, or none at all — expressible without a second client.
 */
export function createAuthenticatedHttpClient(deps: AuthenticatedHttpDeps): HttpClient {
  return {
    send(request: HttpRequest): Promise<HttpResponse> {
      if (!mayAttachAuth(deps.config, request.url)) return deps.http.send(request);

      const alreadySet = Object.keys(request.headers).some((k) => k.toLowerCase() === 'authorization');
      if (alreadySet) return deps.http.send(request);

      const headers = deps.auth.headers();
      if (Object.keys(headers).length === 0) return deps.http.send(request);

      return deps.http.send({ ...request, headers: { ...request.headers, ...headers } });
    },
  };
}
